import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { CheckCircle2, AlertTriangle, XCircle, Info, X } from 'lucide-react';

export type DialogType = 'success' | 'error' | 'warning' | 'info';

export interface AlertOptions {
  title?: string;
  message: string;
  type?: DialogType;
  confirmText?: string;
}

export interface ConfirmOptions {
  title?: string;
  message: string;
  type?: 'danger' | 'warning' | 'info';
  confirmText?: string;
  cancelText?: string;
}

export interface PromptOptions {
  title?: string;
  message: string;
  defaultValue?: string;
  placeholder?: string;
  confirmText?: string;
  cancelText?: string;
}

export interface ToastItem {
  id: string;
  title?: string;
  message: string;
  type: DialogType;
  duration?: number;
}

interface InAppDialogContextType {
  showAlert: (options: AlertOptions | string) => Promise<void>;
  showConfirm: (options: ConfirmOptions | string) => Promise<boolean>;
  showPrompt: (options: PromptOptions | string) => Promise<string | null>;
  showToast: (options: { title?: string; message: string; type?: DialogType; duration?: number } | string) => void;
}

const InAppDialogContext = createContext<InAppDialogContextType | null>(null);

// Global bridge for non-React contexts or window.alert/confirm overrides
type AlertResolver = () => void;
type ConfirmResolver = (value: boolean) => void;
type PromptResolver = (value: string | null) => void;

interface ActiveAlert {
  title: string;
  message: string;
  type: DialogType;
  confirmText: string;
  resolve: AlertResolver;
}

interface ActiveConfirm {
  title: string;
  message: string;
  type: 'danger' | 'warning' | 'info';
  confirmText: string;
  cancelText: string;
  resolve: ConfirmResolver;
}

interface ActivePrompt {
  title: string;
  message: string;
  defaultValue: string;
  placeholder: string;
  confirmText: string;
  cancelText: string;
  resolve: PromptResolver;
}

let globalShowAlert: ((opts: AlertOptions) => Promise<void>) | null = null;
let globalShowConfirm: ((opts: ConfirmOptions) => Promise<boolean>) | null = null;
let globalShowPrompt: ((opts: PromptOptions) => Promise<string | null>) | null = null;
let globalShowToast: ((opts: { title?: string; message: string; type?: DialogType; duration?: number }) => void) | null = null;

export const inAppAlert = (messageOrOpts: string | AlertOptions): Promise<void> => {
  const opts: AlertOptions = typeof messageOrOpts === 'string' ? { message: messageOrOpts } : messageOrOpts;
  if (globalShowAlert) {
    return globalShowAlert(opts);
  }
  // Fallback if called before mount
  return Promise.resolve();
};

export const inAppConfirm = (messageOrOpts: string | ConfirmOptions): Promise<boolean> => {
  const opts: ConfirmOptions = typeof messageOrOpts === 'string' ? { message: messageOrOpts } : messageOrOpts;
  if (globalShowConfirm) {
    return globalShowConfirm(opts);
  }
  // Fallback
  return Promise.resolve(true);
};

export const inAppPrompt = (messageOrOpts: string | PromptOptions): Promise<string | null> => {
  const opts: PromptOptions = typeof messageOrOpts === 'string' ? { message: messageOrOpts } : messageOrOpts;
  if (globalShowPrompt) {
    return globalShowPrompt(opts);
  }
  return Promise.resolve(null);
};

export const inAppToast = (messageOrOpts: string | { title?: string; message: string; type?: DialogType; duration?: number }) => {
  const opts = typeof messageOrOpts === 'string' ? { message: messageOrOpts, type: 'info' as DialogType } : messageOrOpts;
  if (globalShowToast) {
    globalShowToast(opts);
  }
};

export const InAppDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeAlert, setActiveAlert] = useState<ActiveAlert | null>(null);
  const [activeConfirm, setActiveConfirm] = useState<ActiveConfirm | null>(null);
  const [activePrompt, setActivePrompt] = useState<ActivePrompt | null>(null);
  const [promptInput, setPromptInput] = useState('');
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  // Function to show in-app alert
  const showAlert = useCallback((options: AlertOptions | string): Promise<void> => {
    return new Promise((resolve) => {
      const opts: AlertOptions = typeof options === 'string' ? { message: options } : options;
      
      // Auto-detect type if not specified
      let detectedType: DialogType = opts.type || 'info';
      const lower = (opts.message || '').toLowerCase();
      if (!opts.type) {
        if (lower.includes('success') || lower.includes('submitted') || lower.includes('saved') || lower.includes('completed')) {
          detectedType = 'success';
        } else if (lower.includes('error') || lower.includes('failed') || lower.includes('unauthorized') || lower.includes('invalid')) {
          detectedType = 'error';
        } else if (lower.includes('warning') || lower.includes('required') || lower.includes('please confirm') || lower.includes('attention')) {
          detectedType = 'warning';
        }
      }

      let defaultTitle = 'Notice';
      if (detectedType === 'success') defaultTitle = 'Success';
      if (detectedType === 'error') defaultTitle = 'Error';
      if (detectedType === 'warning') defaultTitle = 'Attention';

      setActiveAlert({
        title: opts.title || defaultTitle,
        message: opts.message,
        type: detectedType,
        confirmText: opts.confirmText || 'OK',
        resolve: () => {
          setActiveAlert(null);
          resolve();
        },
      });
    });
  }, []);

  // Function to show in-app confirm
  const showConfirm = useCallback((options: ConfirmOptions | string): Promise<boolean> => {
    return new Promise((resolve) => {
      const opts: ConfirmOptions = typeof options === 'string' ? { message: options } : options;
      
      let defaultTitle = 'Confirm Action';
      let detectedType: 'danger' | 'warning' | 'info' = opts.type || 'info';
      const lower = (opts.message || '').toLowerCase();
      if (!opts.type) {
        if (lower.includes('delete') || lower.includes('revoke') || lower.includes('remove') || lower.includes('archive') || lower.includes('reset')) {
          detectedType = 'danger';
          defaultTitle = 'Confirm Deletion / Action';
        }
      }

      setActiveConfirm({
        title: opts.title || defaultTitle,
        message: opts.message,
        type: detectedType,
        confirmText: opts.confirmText || (detectedType === 'danger' ? 'Confirm' : 'Yes'),
        cancelText: opts.cancelText || 'Cancel',
        resolve: (result: boolean) => {
          setActiveConfirm(null);
          resolve(result);
        },
      });
    });
  }, []);

  // Function to show in-app prompt
  const showPrompt = useCallback((options: PromptOptions | string): Promise<string | null> => {
    return new Promise((resolve) => {
      const opts: PromptOptions = typeof options === 'string' ? { message: options } : options;
      const initialVal = opts.defaultValue || '';
      setPromptInput(initialVal);

      setActivePrompt({
        title: opts.title || 'Input Required',
        message: opts.message,
        defaultValue: initialVal,
        placeholder: opts.placeholder || '',
        confirmText: opts.confirmText || 'OK',
        cancelText: opts.cancelText || 'Cancel',
        resolve: (result: string | null) => {
          setActivePrompt(null);
          resolve(result);
        },
      });
    });
  }, []);

  // Function to show toast
  const showToast = useCallback((options: { title?: string; message: string; type?: DialogType; duration?: number } | string) => {
    const opts = typeof options === 'string' ? { message: options, type: 'info' as DialogType } : options;
    const id = Math.random().toString(36).substring(2, 9);
    const item: ToastItem = {
      id,
      title: opts.title,
      message: opts.message,
      type: opts.type || 'info',
      duration: opts.duration || 4000,
    };

    setToasts((prev) => [...prev, item]);

    if (item.duration && item.duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, item.duration);
    }
  }, []);

  // Register global helpers and hook window.alert / window.confirm / window.prompt
  useEffect(() => {
    globalShowAlert = showAlert;
    globalShowConfirm = showConfirm;
    globalShowPrompt = showPrompt;
    globalShowToast = showToast;

    // Intercept native browser alert, confirm and prompt
    const originalAlert = window.alert;
    const originalConfirm = window.confirm;
    const originalPrompt = window.prompt;

    window.alert = (message?: any) => {
      const msgStr = typeof message === 'string' ? message : JSON.stringify(message, null, 2);
      showAlert({ message: msgStr });
    };

    window.confirm = (message?: string) => {
      showConfirm({ message: message || 'Please confirm this action' });
      return true;
    };

    window.prompt = (message?: string, _default?: string) => {
      showPrompt({ message: message || 'Enter value', defaultValue: _default });
      return _default !== undefined ? _default : '';
    };

    return () => {
      globalShowAlert = null;
      globalShowConfirm = null;
      globalShowPrompt = null;
      globalShowToast = null;
      window.alert = originalAlert;
      window.confirm = originalConfirm;
      window.prompt = originalPrompt;
    };
  }, [showAlert, showConfirm, showPrompt, showToast]);

  // Handle ESC key to dismiss
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeAlert) {
          activeAlert.resolve();
        } else if (activeConfirm) {
          activeConfirm.resolve(false);
        } else if (activePrompt) {
          activePrompt.resolve(null);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeAlert, activeConfirm, activePrompt]);

  return (
    <InAppDialogContext.Provider value={{ showAlert, showConfirm, showToast }}>
      {children}

      {/* In-Window Alert Modal */}
      <AnimatePresence>
        {activeAlert && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => activeAlert.resolve()}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ type: 'spring', damping: 25, stiffness: 350 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10"
            >
              {/* Header colored stripe */}
              <div
                className={`h-2 w-full ${
                  activeAlert.type === 'success'
                    ? 'bg-emerald-500'
                    : activeAlert.type === 'error'
                    ? 'bg-rose-500'
                    : activeAlert.type === 'warning'
                    ? 'bg-amber-500'
                    : 'bg-[#0094c8]'
                }`}
              />

              <div className="p-6">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                      activeAlert.type === 'success'
                        ? 'bg-emerald-50 text-emerald-600'
                        : activeAlert.type === 'error'
                        ? 'bg-rose-50 text-rose-600'
                        : activeAlert.type === 'warning'
                        ? 'bg-amber-50 text-amber-600'
                        : 'bg-sky-50 text-[#0094c8]'
                    }`}
                  >
                    {activeAlert.type === 'success' && <CheckCircle2 className="w-7 h-7" />}
                    {activeAlert.type === 'error' && <XCircle className="w-7 h-7" />}
                    {activeAlert.type === 'warning' && <AlertTriangle className="w-7 h-7" />}
                    {activeAlert.type === 'info' && <Info className="w-7 h-7" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-900 leading-snug">
                      {activeAlert.title}
                    </h3>
                    <div className="mt-2 text-sm text-slate-600 leading-relaxed break-words whitespace-pre-wrap max-h-60 overflow-y-auto pr-1">
                      {activeAlert.message}
                    </div>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => activeAlert.resolve()}
                    className={`px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-white shadow-sm transition-all cursor-pointer ${
                      activeAlert.type === 'success'
                        ? 'bg-emerald-600 hover:bg-emerald-700'
                        : activeAlert.type === 'error'
                        ? 'bg-rose-600 hover:bg-rose-700'
                        : activeAlert.type === 'warning'
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-[#0094c8] hover:bg-[#007ba6]'
                    }`}
                  >
                    {activeAlert.confirmText}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* In-Window Confirmation Modal */}
      <AnimatePresence>
        {activeConfirm && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => activeConfirm.resolve(false)}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ type: 'spring', damping: 25, stiffness: 350 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10"
            >
              {/* Header colored stripe */}
              <div
                className={`h-2 w-full ${
                  activeConfirm.type === 'danger'
                    ? 'bg-rose-500'
                    : activeConfirm.type === 'warning'
                    ? 'bg-amber-500'
                    : 'bg-[#0094c8]'
                }`}
              />

              <div className="p-6">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                      activeConfirm.type === 'danger'
                        ? 'bg-rose-50 text-rose-600'
                        : activeConfirm.type === 'warning'
                        ? 'bg-amber-50 text-amber-600'
                        : 'bg-sky-50 text-[#0094c8]'
                    }`}
                  >
                    {activeConfirm.type === 'danger' && <AlertTriangle className="w-7 h-7" />}
                    {activeConfirm.type === 'warning' && <AlertTriangle className="w-7 h-7" />}
                    {activeConfirm.type === 'info' && <Info className="w-7 h-7" />}
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-900 leading-snug">
                      {activeConfirm.title}
                    </h3>
                    <div className="mt-2 text-sm text-slate-600 leading-relaxed break-words whitespace-pre-wrap max-h-60 overflow-y-auto pr-1">
                      {activeConfirm.message}
                    </div>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => activeConfirm.resolve(false)}
                    className="px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-all cursor-pointer"
                  >
                    {activeConfirm.cancelText}
                  </button>
                  <button
                    type="button"
                    onClick={() => activeConfirm.resolve(true)}
                    className={`px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-white shadow-sm transition-all cursor-pointer ${
                      activeConfirm.type === 'danger'
                        ? 'bg-rose-600 hover:bg-rose-700'
                        : activeConfirm.type === 'warning'
                        ? 'bg-amber-600 hover:bg-amber-700'
                        : 'bg-[#0094c8] hover:bg-[#007ba6]'
                    }`}
                  >
                    {activeConfirm.confirmText}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* In-Window Prompt Modal */}
      <AnimatePresence>
        {activePrompt && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => activePrompt.resolve(null)}
              className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
            />

            {/* Modal Box */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ type: 'spring', damping: 25, stiffness: 350 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden z-10"
            >
              {/* Header colored stripe */}
              <div className="h-2 w-full bg-[#0094c8]" />

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  activePrompt.resolve(promptInput);
                }}
                className="p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center bg-sky-50 text-[#0094c8]">
                    <Info className="w-7 h-7" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-slate-900 leading-snug">
                      {activePrompt.title}
                    </h3>
                    <div className="mt-1 text-sm text-slate-600 leading-relaxed break-words whitespace-pre-wrap">
                      {activePrompt.message}
                    </div>

                    <div className="mt-4">
                      <input
                        type="text"
                        autoFocus
                        value={promptInput}
                        onChange={(e) => setPromptInput(e.target.value)}
                        placeholder={activePrompt.placeholder}
                        className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-800 focus:bg-white focus:border-[#0094c8] focus:ring-2 focus:ring-[#0094c8]/20 outline-none transition-all"
                      />
                    </div>
                  </div>
                </div>

                {/* Footer Buttons */}
                <div className="mt-6 flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => activePrompt.resolve(null)}
                    className="px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-slate-600 hover:text-slate-800 hover:bg-slate-100 transition-all cursor-pointer"
                  >
                    {activePrompt.cancelText}
                  </button>
                  <button
                    type="submit"
                    className="px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider text-white bg-[#0094c8] hover:bg-[#007ba6] shadow-sm transition-all cursor-pointer"
                  >
                    {activePrompt.confirmText}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Floating In-Window Toast Notifications */}
      <div className="fixed top-5 right-5 z-[99999] flex flex-col gap-2.5 max-w-sm w-full pointer-events-none">
        <AnimatePresence>
          {toasts.map((toast) => (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, x: 50, scale: 0.95 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 50, scale: 0.95 }}
              transition={{ duration: 0.2 }}
              className="pointer-events-auto flex items-start gap-3 p-4 bg-white/95 backdrop-blur-md rounded-xl shadow-xl border border-slate-200/80 text-slate-800 overflow-hidden"
            >
              <div
                className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
                  toast.type === 'success'
                    ? 'bg-emerald-100 text-emerald-600'
                    : toast.type === 'error'
                    ? 'bg-rose-100 text-rose-600'
                    : toast.type === 'warning'
                    ? 'bg-amber-100 text-amber-600'
                    : 'bg-sky-100 text-[#0094c8]'
                }`}
              >
                {toast.type === 'success' && <CheckCircle2 className="w-5 h-5" />}
                {toast.type === 'error' && <XCircle className="w-5 h-5" />}
                {toast.type === 'warning' && <AlertTriangle className="w-5 h-5" />}
                {toast.type === 'info' && <Info className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                {toast.title && (
                  <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wide">
                    {toast.title}
                  </h4>
                )}
                <p className="text-xs text-slate-600 mt-0.5 leading-snug">{toast.message}</p>
              </div>
              <button
                type="button"
                onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-md transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </InAppDialogContext.Provider>
  );
};

export const useInAppDialog = () => {
  const context = useContext(InAppDialogContext);
  if (!context) {
    return {
      showAlert: inAppAlert,
      showConfirm: inAppConfirm,
      showPrompt: inAppPrompt,
      showToast: inAppToast,
    };
  }
  return context;
};
